import { AlertTriangle } from "lucide-react";

interface QueryErrorStateProps {
  error: { message: string };
  /** What failed to load, e.g. "the longlist". Defaults to "this view". */
  what?: string;
  onRetry?: () => void;
  retrying?: boolean;
}

// urql prefixes messages with "[GraphQL] " / "[Network] "; the prefix is
// noise to a recruiter.
export function describeQueryError(error: { message: string }): string {
  const message = error.message
    .split("\n")
    .map((line) => line.replace(/^\[(GraphQL|Network)\]\s*/, "").trim())
    .filter(Boolean)
    .join(" ");
  return message || "Something went wrong.";
}

/**
 * Replaces a list's empty state when its query failed (#3172). Before this
 * existed a failed request rendered "No candidates yet", which is a lie the
 * user cannot tell from the truth.
 */
export function QueryErrorState({ error, what, onRetry, retrying }: QueryErrorStateProps) {
  return (
    <div
      role="alert"
      className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <div>
            <p className="font-medium">Couldn't load {what ?? "this view"}</p>
            <p className="mt-1 text-xs">{describeQueryError(error)}</p>
          </div>
        </div>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            className="shrink-0 text-xs underline hover:no-underline disabled:opacity-50"
          >
            {retrying ? "Retrying…" : "Retry"}
          </button>
        )}
      </div>
    </div>
  );
}
